import { action, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { Id } from '@/infra/id'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import type { OCRScopeWorkStore } from '@/features/jobs/scoping/OCRScopeWorkStore'

/**
 * Dialog to confirm and archive an OCR scope work.
 */
export class OCRConfirmArchiveDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<boolean>({
    abort$: this.modal.close$,
  })

  private _scopeWorkId: Id = Id.none

  constructor(
    private readonly _ocrScopeWorkStore: OCRScopeWorkStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Prompts the user to archive the scope work.
   * @returns Promise resolving to true if archived, false if canceled
   */
  async prompt({ scopeWorkId, signal }: PromptScopeWorkOptions) {
    this.modal.close()

    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this._scopeWorkId = scopeWorkId
      this.modal.show()
    })

    const response = await responsePromise
    this.modal.close()
    return response ?? false
  }

  @action.bound
  cancel() {
    this.modal.close()
  }

  /**
   * Closes the dialog right away; the request keeps the scope work's actions
   * pending until it settles.
   */
  archive = task.resolved(async () => {
    this._prompt.respond(true)
    this.modal.close()

    await this._ocrScopeWorkStore
      .archive(this._scopeWorkId)
      .catch(this._createAlertErrorHandler('Archiving the scoping job failed'))
    this._newAlert()
      .saying('Success!', 'Scoping job was archived successfully.')
      .withState('success')
      .autoDismiss()
      .show()
  })
}

/**
 * Options for prompting about a scope work.
 */
export interface PromptScopeWorkOptions {
  scopeWorkId: Id
  signal?: AbortSignal
}
